import { z } from 'zod';

import {
  EntityBaseSchema,
  EntityMetadataSchema,
  NonEmptyStringSchema,
} from '../../base';

// -- Function Call Assertion sub-schemas --

export const AssertionValueType = {
  STRING: 'string',
  INTEGER: 'integer',
  FLOAT: 'float',
  BOOLEAN: 'boolean',
} as const;

export const AssertionType = {
  EQUALS: 'equals',
  ANY: 'any',
} as const;

export const FunctionCallAssertionArgumentSchema = z
  .object({
    valueType: z
      .enum(['string', 'integer', 'float', 'boolean'])
      .default('string')
      .describe(
        'Data type for comparison (float uses tolerance via math.isclose)',
      ),
    assertionType: z
      .enum(['equals', 'any'])
      .describe('Assertion operator: equals (exact match), any (present)'),
    expectedValue: z
      .union([z.string(), z.number(), z.boolean()])
      .nullable()
      .describe('Value to compare against (null for assertionType=any)'),
  })
  .describe('A single argument assertion within a function call assertion');

export type FunctionCallAssertionArgument = z.infer<
  typeof FunctionCallAssertionArgumentSchema
>;

export const FunctionCallAssertionSchema = z
  .object({
    name: z.string().describe('Name of the function expected to be called'),
    arguments: z
      .record(z.string(), FunctionCallAssertionArgumentSchema)
      .describe('Map of argument name to its assertion configuration'),
    isAsserted: z
      .boolean()
      .describe('Whether this function call assertion is active'),
  })
  .describe(
    'An assertion that a specific function is called with expected arguments during a test case',
  );

export type FunctionCallAssertion = z.infer<typeof FunctionCallAssertionSchema>;

// -- Test Case schema --

export const TestCaseSchema = EntityBaseSchema.extend({
  name: NonEmptyStringSchema.describe('Human-readable name of the test case'),
  scenario: NonEmptyStringSchema.describe(
    'The conversation scenario or prompt to simulate',
  ),
  assertions: z
    .array(z.string())
    .describe('List of text assertions to validate against the conversation'),
  functionCalls: z
    .array(FunctionCallAssertionSchema)
    .describe('List of function call assertions to validate'),
  variantId: z
    .string()
    .nullable()
    .describe(
      'Optional variant ID to run the test case against a specific agent variant',
    ),
  expectedOutcome: z
    .string()
    .describe(
      'Success criteria for LLM evaluation — what the agent should achieve',
    ),
  language: z.string().describe('Language code for the test case conversation'),
  tags: z
    .array(z.string())
    .describe('Tags for categorizing and filtering test cases'),
  simulatedAt: z
    .string()
    .nullable()
    .describe('ISO timestamp for the simulated conversation time'),
}).merge(EntityMetadataSchema);

export type TestCase = z.infer<typeof TestCaseSchema>;
